import type { SignalCategory } from '@routr/shared'
import { SDI_RATE_LABELS, rateForFormat, type SdiRate } from '@routr/shared'
import {
  DEFAULT_GRADE,
  DEFAULT_HDMI_GRADE,
  GRADE_INFO,
  HDMI_GRADE_INFO,
  reachFor,
  type CableGrade,
  type HdmiGrade,
} from '@routr/shared'

/**
 * Cables, as a crew loads them.
 *
 * A diagram tells you what connects to what; a pack list tells you what goes
 * in the van. Those are different questions, and the second one is grouped by
 * *type and length* rather than by run: nobody packs "the cable from CAM-3 to
 * the switcher", they pack four twenty-metre SDI leads.
 *
 * Pure, so the arithmetic is testable without a browser and the screen, the CSV
 * and the PDF cannot disagree.
 *
 * Two things this deliberately does NOT do:
 *
 *   - Guess an SDI data rate. Only 8 of 1,845 SDI ports in the catalogue name
 *     one, so the reach limit for coax is given as guidance with its dependence
 *     stated, never as a hard number the app pretends to know.
 *   - Derive length from the canvas. Node coordinates are layout, not distance.
 */

export type CableFamily = {
  key: string
  label: string
  /** Lengths you can actually buy or hire, in metres. */
  stock: number[]
  /** Guidance limit in metres, or null where distance is not the constraint. */
  maxReachM: number | null
  /** Why that limit exists, and what it depends on. Shown with any warning. */
  reachNote: string | null
}

/**
 * Connector to cable family. The connector is what the port says; the family is
 * what you order. Keyed on the connector strings in `signals.ts`.
 */
const FAMILY_BY_CONNECTOR: Record<string, CableFamily> = {}

function family(f: CableFamily, connectors: string[]): CableFamily {
  for (const connector of connectors) FAMILY_BY_CONNECTOR[connector] = f
  return f
}

const COAX_STOCK = [0.5, 1, 2, 3, 5, 10, 15, 20, 30, 50, 75, 100]

export const SDI_COAX = family(
  {
    key: 'sdi-coax',
    label: 'SDI coax',
    stock: COAX_STOCK,
    maxReachM: 100,
    reachNote:
      'Reach depends on the data rate and the cable grade: HD-SDI runs past 150 m on good coax, 12G-SDI often gives up around 45 m. Check the rate on this run.',
  },
  ['BNC (SDI)', 'HD-BNC', 'Mini-BNC', 'Micro BNC', 'Mini DIN 1.0/2.3', 'Composite (BNC)'],
)

export const HDMI = family(
  {
    key: 'hdmi',
    label: 'HDMI',
    stock: [0.5, 1, 2, 3, 5, 7.5, 10, 15, 20],
    maxReachM: 15,
    reachNote:
      'Passive HDMI is unreliable much past 15 m. Beyond that use an active/optical lead or an extender.',
  },
  ['HDMI', 'Micro HDMI', 'Mini HDMI'],
)

export const CAT = family(
  {
    key: 'cat',
    label: 'Cat6 / network',
    stock: [0.5, 1, 2, 3, 5, 10, 15, 20, 30, 50, 75, 100],
    maxReachM: 100,
    reachNote: 'Ethernet is specified to 100 m including patch leads. Past that you need fibre or a switch in between.',
  },
  ['RJ45', 'RJ45 (RS-422)'],
)

export const HDBASET = family(
  {
    key: 'hdbaset',
    label: 'HDBaseT (Cat6)',
    stock: [1, 2, 3, 5, 10, 15, 20, 30, 50, 70, 100],
    maxReachM: 100,
    reachNote: 'HDBaseT is specified to 100 m at 1080p and commonly less at 4K. Check the extender’s own figure.',
  },
  ['RJ45 (HDBaseT)'],
)

export const FIBRE = family(
  {
    key: 'fibre',
    label: 'Fibre',
    stock: [3, 5, 10, 20, 30, 50, 100, 150, 200, 300, 500],
    maxReachM: null,
    reachNote: null,
  },
  ['LC Fiber', 'SC Fiber', 'Optical (SMPTE 311M)', 'SFP+', 'SFP28'],
)

export const XLR = family(
  { key: 'xlr', label: 'XLR audio', stock: [1, 2, 3, 5, 10, 15, 20, 30, 50], maxReachM: null, reachNote: null },
  ['XLR3', 'XLR5', 'XLR Combo', 'TA3', 'TA5'],
)

export const JACK = family(
  { key: 'jack', label: 'Jack audio', stock: [0.5, 1, 2, 3, 5, 10, 15, 20], maxReachM: null, reachNote: null },
  ['TRS 1/4"', 'TRS 3.5mm', 'RCA', 'Component (RCA)', 'Composite (RCA)', 'S-Video'],
)

export const AES = family(
  { key: 'aes', label: 'AES / MADI', stock: COAX_STOCK, maxReachM: 100, reachNote: 'Digital audio over coax; grade dependent.' },
  ['MADI BNC', 'BNC (AES3id)', 'DB25 (AES59)'],
)

export const SPEAKER = family(
  { key: 'speaker', label: 'Speaker', stock: [1, 3, 5, 10, 15, 20, 30, 50], maxReachM: null, reachNote: null },
  ['Speakon NL4'],
)

export const USB = family(
  {
    key: 'usb',
    label: 'USB',
    stock: [0.5, 1, 2, 3, 5],
    maxReachM: 5,
    reachNote: 'Passive USB runs out around 5 m, and around 3 m for USB 3. Longer needs an active lead.',
  },
  ['USB-A', 'USB-B', 'USB-C', 'Micro USB', 'USB 3.0 (Type-B)', 'USB-C (DP Alt)', 'Thunderbolt'],
)

export const DISPLAY = family(
  {
    key: 'display',
    label: 'DisplayPort / DVI / VGA',
    stock: [1, 2, 3, 5, 10, 15],
    maxReachM: 3,
    reachNote: 'Passive DisplayPort is specified to 3 m at full rate. DVI and VGA degrade past about 5 m.',
  },
  ['DisplayPort', 'Mini DisplayPort', 'DVI-D', 'VGA (HD15)'],
)

export const SERIAL = family(
  { key: 'serial', label: 'Serial / control', stock: [1, 2, 3, 5, 10, 15, 20, 30], maxReachM: null, reachNote: null },
  ['DB9 (RS-422)', 'DB9 (Tally)', 'DB9 (RS-232)', 'Phoenix (RS-232)', 'Mini-DIN 8 (RS-232)', 'Phoenix (Euroblock)', 'IR (3.5mm)', 'LANC (2.5mm)'],
)

export const MAINS = family(
  { key: 'mains', label: 'Mains', stock: [0.5, 1, 2, 3, 5, 10, 15, 20], maxReachM: null, reachNote: null },
  ['IEC C13', 'IEC C14', 'IEC C19', 'IEC C20', 'powerCON', 'powerCON TRUE1', 'Schuko', 'IEC C7', 'IEC C8'],
)

export const DC = family(
  { key: 'dc', label: 'DC power', stock: [0.5, 1, 2, 3, 5, 10], maxReachM: null, reachNote: null },
  ['DC Barrel 12V', 'DC Barrel 5V', 'DC Barrel 24V', 'XLR4 (DC)', 'Hirose 4-pin', 'LEMO', '4-pin Molex', 'USB-C (Power)', 'Micro USB (Power)', 'USB Mini-B (Power)'],
)

export const OTHER = {
  key: 'other',
  label: 'Other',
  stock: [0.5, 1, 2, 3, 5, 10, 15, 20, 30, 50],
  maxReachM: null,
  reachNote: null,
} satisfies CableFamily

export function familyForConnector(connector: string): CableFamily {
  return FAMILY_BY_CONNECTOR[connector] ?? OTHER
}

/* --------------------------------------------------------------- the type */

export type CableType = {
  /** Stable grouping key. Same key means the same thing goes in the case. */
  key: string
  label: string
  signal: SignalCategory
  family: CableFamily
  /** True when the two ends differ — the leads people forget to pack. */
  adapter: boolean
}

/**
 * What to order for one run.
 *
 * Two identical connectors is a plain lead. Two different ones is an adapter
 * lead, and it gets its own line: "HDMI to Micro HDMI" is a thing you either
 * brought or did not, and it never turns up in a box of HDMI cables.
 */
export function cableTypeFor(
  signal: SignalCategory,
  a: string,
  b: string,
): CableType {
  const familyA = familyForConnector(a)
  const familyB = familyForConnector(b)

  if (a === b) {
    // Connector names already carry their own parenthetical — "BNC (SDI)" —
    // so nesting them inside the family gives "SDI coax (BNC (SDI))". A middot
    // is the separator the rest of the app uses for exactly this.
    const label = a.toLowerCase().includes(familyA.label.toLowerCase()) ? a : `${familyA.label} · ${a}`
    return { key: `${signal}:${familyA.key}:${a}`, label, signal, family: familyA, adapter: false }
  }
  // Same family, different ends — a BNC to HD-BNC lead, say. Still one cable,
  // still an adapter, and the stock ladder of the first end is the right one.
  const [left, right] = [a, b].sort()
  return {
    key: `${signal}:${familyA.key}-${familyB.key}:${left}>${right}`,
    label: `${left} to ${right}`,
    signal,
    family: familyA,
    adapter: true,
  }
}

/* ---------------------------------------------------------- the pack list */

/** Round up to something you can actually get hold of. */
export function roundToStock(metres: number, stock: number[]): number {
  if (!(metres > 0)) return 0
  const fit = stock.find((length) => length >= metres)
  if (fit != null) return fit
  // Past the longest stock length, round up to a whole multiple of it rather
  // than inventing a size: two 100 m drums is a real answer, 137 m is not.
  const longest = stock[stock.length - 1] ?? metres
  return Math.ceil(metres / longest) * longest
}

export type SpareRule = { kind: 'NONE' } | { kind: 'PER_TYPE'; each: number } | { kind: 'PERCENT'; pct: number }

/**
 * A cable you can order, with the reach its maker publishes.
 *
 * A null in `reach` means the manufacturer does not publish that rate — Canare,
 * for instance, publish no 6G-SDI figure for any cable they make. That is a
 * different thing from "no limit", and the two must never be conflated.
 */
export type CableProduct = {
  id: string
  manufacturer: string
  partNumber: string
  name: string
  family: string
  connector: string
  reach: Record<SdiRate, number | null>
  stockLengthsM: number[]
  sourceUrl: string | null
  notes: string | null
}

export type CableRun = {
  id: string
  signal: SignalCategory
  sourceConnector: string
  targetConnector: string
  lengthM: number | null
  /** For the reach warning to name the run. */
  label: string
  /** Coax grade or HDMI certification. Null takes the sensible default. */
  cableGrade?: string | null
  /** Overrides the show format for this run only. */
  formatOverride?: string | null
  /** The actual product, when the run names one. Beats the grade. */
  product?: CableProduct | null
}

export type PackLine = { lengthM: number; count: number; spares: number }

export type PackGroup = {
  type: CableType
  lines: PackLine[]
  cables: number
  spares: number
  metres: number
}

export type ReachWarning = {
  /** Unique per run: several cables can share a label between the same pair. */
  runIds: string[]
  runLabel: string
  /** How many runs share this exact problem. */
  count: number
  typeLabel: string
  lengthM: number
  limitM: number
  note: string
  /** The rate that produced this limit, so the warning can say why. */
  rate: SdiRate | null
  gradeLabel: string
}

/**
 * How far this particular run will carry this particular signal.
 *
 * For coax the answer depends on both the data rate and the grade of cable, so
 * a 60 m camera run is comfortable at HD on standard coax and hopeless at 12G
 * on the same drum. For HDMI it depends on the certification instead. Where
 * neither applies — XLR, fibre, mains — distance is not the constraint and the
 * honest answer is that there is no limit to state.
 */
export function reachForRun(
  type: CableType,
  grade: string | null | undefined,
  formatKey: string | null | undefined,
  product?: CableProduct | null,
): { limitM: number | null; note: string; rate: SdiRate | null; gradeLabel: string } {
  // A named product beats every class typical: these are the maker's own
  // published distances for this exact cable, not the sort of number cables
  // like it tend to manage.
  if (product) {
    const rate = rateForFormat(formatKey)
    if (rate == null) {
      return {
        limitM: null,
        note: `Set the show format to check this run against ${product.manufacturer}'s figures for the ${product.partNumber}.`,
        rate: null,
        gradeLabel: product.partNumber,
      }
    }
    const published = product.reach[rate] ?? null
    if (published == null) {
      // Silence, not an interpolation between the rates either side. Canare
      // publish no 6G figure for anything, and inventing one here would be
      // exactly the confident-and-wrong number this module exists to avoid.
      return {
        limitM: null,
        note: `${product.manufacturer} publish no ${SDI_RATE_LABELS[rate]} figure for the ${product.partNumber}, so this run cannot be checked against it.`,
        rate,
        gradeLabel: product.partNumber,
      }
    }
    return {
      limitM: published,
      note: `${product.manufacturer} publish ${published} m for the ${product.partNumber} at ${SDI_RATE_LABELS[rate]}. That is a cable-loss figure, not a guaranteed link — the receiver's equalisation has the final say.`,
      rate,
      gradeLabel: `${product.manufacturer} ${product.partNumber}`,
    }
  }

  if (type.family.key === 'hdmi') {
    const hdmi = HDMI_GRADE_INFO[(grade as HdmiGrade) ?? DEFAULT_HDMI_GRADE] ?? HDMI_GRADE_INFO[DEFAULT_HDMI_GRADE]
    return { limitM: hdmi.reachM, note: hdmi.detail, rate: null, gradeLabel: hdmi.label }
  }

  const coax = ['sdi-coax', 'aes'].includes(type.family.key)
  if (!coax) {
    // Falls back to the family's own guidance — Ethernet's 100 m, USB's 5 m —
    // which does not vary with a video format.
    return { limitM: type.family.maxReachM, note: type.family.reachNote ?? '', rate: null, gradeLabel: '' }
  }

  const info = GRADE_INFO[(grade as CableGrade) ?? DEFAULT_GRADE] ?? GRADE_INFO[DEFAULT_GRADE]
  const rate = rateForFormat(formatKey)
  if (rate == null) {
    // No show format set, so the rate is unknown. Saying nothing beats
    // inventing a limit: the app does not know whether this is HD or 12G.
    return {
      limitM: null,
      note: 'Set the show format to check this run — reach depends entirely on the data rate.',
      rate: null,
      gradeLabel: info.label,
    }
  }
  return {
    limitM: reachFor(info.key, rate),
    note: `${info.label}: ${info.detail} Figures are typical for the class, not a specific product.`,
    rate,
    gradeLabel: info.label,
  }
}

export type PackList = {
  groups: PackGroup[]
  totalCables: number
  totalSpares: number
  totalMetres: number
  /** Runs with no length yet. Counted, named, and in no total. */
  unmeasured: { id: string; label: string; typeLabel: string }[]
  /**
   * Runs longer than any length the named product is sold in. Named rather
   * than packed: rounding a 120 m run up to "1 x 120 m of Canare L-4CHD" puts
   * a cable on the list that cannot be bought, which is worse than saying so.
   */
  tooLong: { id: string; label: string; product: string; lengthM: number; longestM: number }[]
  warnings: ReachWarning[]
}

/**
 * Groups runs into what to pack.
 *
 * A run with no length is NOT rounded to a guess and NOT silently dropped: it
 * is listed separately, because a pack list that quietly omits four cables
 * sends a crew out short, which is the failure this whole feature exists to
 * prevent.
 */
export function buildPackList(
  runs: CableRun[],
  spareRule: SpareRule = { kind: 'NONE' },
  showFormat: string | null = null,
): PackList {
  const groups = new Map<string, { type: CableType; counts: Map<number, number> }>()
  const unmeasured: PackList['unmeasured'] = []
  const tooLong: PackList['tooLong'] = []
  const warnings: ReachWarning[] = []
  const warningsByKey = new Map<string, ReachWarning>()

  for (const run of runs) {
    const type = cableTypeFor(run.signal, run.sourceConnector, run.targetConnector)

    if (run.lengthM == null || !(run.lengthM > 0)) {
      unmeasured.push({ id: run.id, label: run.label, typeLabel: type.label })
      continue
    }

    const reach = reachForRun(type, run.cableGrade, run.formatOverride ?? showFormat, run.product)
    if (reach.limitM != null && run.lengthM > reach.limitM) {
      // Grouped as they are collected: four cameras on the same over-long run
      // is one problem to fix, not four lines that scroll the real ones away.
      const key = `${run.label}|${type.key}|${run.lengthM}|${reach.limitM}`
      const seen = warningsByKey.get(key)
      if (seen) {
        seen.runIds.push(run.id)
        seen.count += 1
      } else {
        const warning: ReachWarning = {
          runIds: [run.id],
          runLabel: run.label,
          count: 1,
          typeLabel: type.label,
          lengthM: run.lengthM,
          limitM: reach.limitM,
          note: reach.note,
          rate: reach.rate,
          gradeLabel: reach.gradeLabel,
        }
        warningsByKey.set(key, warning)
        warnings.push(warning)
      }
    }

    // A named product is its own line, rounded to the lengths it is actually
    // sold in — "4 × Canare L-4CHD 20 m" is orderable, "4 × SDI coax 20 m" is
    // a shopping trip.
    const ladder = run.product?.stockLengthsM?.length ? run.product.stockLengthsM : type.family.stock

    // A generic family ladder is a guide, so running past it means "two drums".
    // A product's ladder is a fact about what the maker sells, so running past
    // THAT means the cable does not exist at this length.
    if (run.product?.stockLengthsM?.length) {
      const longest = ladder[ladder.length - 1] ?? 0
      if (run.lengthM > longest) {
        tooLong.push({
          id: run.id,
          label: run.label,
          product: `${run.product.manufacturer} ${run.product.partNumber}`,
          lengthM: run.lengthM,
          longestM: longest,
        })
        continue
      }
    }

    const stocked = roundToStock(run.lengthM, ladder)
    const groupKey = run.product ? `${type.key}::${run.product.id}` : type.key
    const groupType: CableType = run.product
      ? { ...type, key: groupKey, label: `${run.product.manufacturer} ${run.product.partNumber}` }
      : type
    const group = groups.get(groupKey) ?? { type: groupType, counts: new Map<number, number>() }
    group.counts.set(stocked, (group.counts.get(stocked) ?? 0) + 1)
    groups.set(groupKey, group)
  }

  const built: PackGroup[] = [...groups.values()]
    .map(({ type, counts }) => {
      const lines: PackLine[] = [...counts]
        .sort((a, b) => a[0] - b[0])
        .map(([lengthM, count]) => ({ lengthM, count, spares: 0 }))
      const cables = lines.reduce((sum, line) => sum + line.count, 0)
      const spares = sparesFor(cables, spareRule)
      // Spares go on the longest length in the group: a spare that is too short
      // is not a spare, and a longer one always covers a shorter run.
      const last = lines[lines.length - 1]
      if (spares > 0 && last) last.spares = spares
      const metres = lines.reduce((sum, line) => sum + line.lengthM * (line.count + line.spares), 0)
      return { type, lines, cables, spares, metres }
    })
    // Busiest first: the thing you need most of is the thing to check first.
    .sort((a, b) => b.cables + b.spares - (a.cables + a.spares) || a.type.label.localeCompare(b.type.label))

  return {
    groups: built,
    totalCables: built.reduce((sum, group) => sum + group.cables, 0),
    totalSpares: built.reduce((sum, group) => sum + group.spares, 0),
    totalMetres: built.reduce((sum, group) => sum + group.metres, 0),
    unmeasured,
    tooLong,
    warnings,
  }
}

function sparesFor(cables: number, rule: SpareRule): number {
  if (cables === 0) return 0
  if (rule.kind === 'PER_TYPE') return rule.each
  if (rule.kind === 'PERCENT') return Math.ceil((cables * rule.pct) / 100)
  return 0
}

/** "4 × 20 m" — the unit of a pack list. */
export function formatPackLine(line: PackLine): string {
  const total = line.count + line.spares
  const length = Number.isInteger(line.lengthM) ? line.lengthM : line.lengthM.toFixed(1)
  return `${total} × ${length} m${line.spares > 0 ? ` (${line.count} + ${line.spares} spare)` : ''}`
}
